import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  Dimmer,
  Grid,
  GridColumn,
  Icon,
  Label,
  Loader,
  Message,
  Segment,
  Table,
  Accordion,
  AccordionTitle,
  AccordionContent,
  Pagination,
  Input,
} from "semantic-ui-react";
import { getFaults } from "../../libs/requests/getFaults";
import { authContext } from "../../containers/AuthProvider";
import Panel from "../Panel";

const OpenFaults = () => {
  const { permissions } = useContext(authContext);
  const [openFaults, setOpenFaults] = useState([]);
  const [closedFaults, setClosedFaults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");

  const [openFaultsPage, setOpenFaultsPage] = useState(1);
  const [closedFaultsPage, setClosedFaultsPage] = useState(1);
  const itemsPerPage = 5;

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [expandedUpdates, setExpandedUpdates] = useState({});

  const toggleExpanded = (faultRef) => {
    setExpandedUpdates((prev) => ({
      ...prev,
      [faultRef]: !prev[faultRef],
    }));
  };

  useEffect(() => {
    async function fetchFaults() {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getFaults(["OpenFaults", "ClosedFaults"]);


        if (response.openFaults?.faults) {
          setOpenFaults(response.openFaults.faults || []);
        }

        if (response.closedFaults?.faults) {
          setClosedFaults(response.closedFaults.faults || []);
        }
      } catch (error) {
        console.error("Error fetching faults:", error);
        setError("Failed to load faults. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    }

    if (permissions?.servicesAccess) {
      fetchFaults();
    }
  }, [permissions?.servicesAccess]);

  const handleSort = (clickedColumn) => {
    if (sortColumn !== clickedColumn) {
      setSortColumn(clickedColumn);
      setSortDirection("ascending");
      return;
    }
    setSortDirection(sortDirection === "ascending" ? "descending" : "ascending");
  };

  const renderUpdateText = (update, faultRef) => {
    const isExpanded = expandedUpdates[faultRef];
    if (!update) return "";
  
    if (update.length <= 50) return update;
  
    return (
      <>
        {isExpanded ? update : `${update.substring(0, 50)}... `}
        <span
          onClick={() => toggleExpanded(faultRef)}
          style={{ color: "#2185d0", cursor: "pointer", userSelect: "none" }}
        >
          {isExpanded ? "Read less" : "Read more"}
        </span>
      </>
    );
  };

  function cleanText(input) {
    if (!input) return "";
  
    let cleaned = input;
  
    // Convert escaped newlines/tabs (\n, \r, \t) appearing as literal chars to real chars
    cleaned = cleaned.replace(/\\r\\n|\\n|\\r/g, "\n").replace(/\\t/g, "\t");
  
    // Remove non-printable/control chars except \n, \r, \t (keep those for formatting)
    cleaned = cleaned.replace(/[^\x20-\x7E\n\r\t]/g, "");
  
    // Normalize line endings to \n only
    cleaned = cleaned.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  
    // Replace Unicode smart quotes and dashes with ascii equivalents
    cleaned = cleaned
      .replace(/[\u2018\u2019\u02BC\u201B]/g, "'") // various apostrophes
      .replace(/[\u201C\u201D\u201E\u201F]/g, '"') // various double quotes
      .replace(/[\u2013]/g, "-")                   // en dash
      .replace(/[\u2014]/g, "--")                  // em dash (double dash)
      .replace(/[\u2026]/g, "...");                 // ellipsis
  
    // Normalize spaces: replace non-breaking spaces and other unicode spaces with normal space
    cleaned = cleaned.replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, " ");
  
    // Remove trailing spaces and tabs at ends of lines
    cleaned = cleaned.replace(/[ \t]+$/gm, "");
  
    // Replace multiple spaces or tabs with single space
    cleaned = cleaned.replace(/[ \t]+/g, " ");
  
    // Collapse 3+ newlines to max 2
    cleaned = cleaned.replace(/\n{3,}/g, "\n\n");
  
    // Remove spaces/tabs immediately after or before newlines
    cleaned = cleaned.replace(/\n[ \t]+/g, "\n").replace(/[ \t]+\n/g, "\n");
  
    // Trim overall text
    cleaned = cleaned.trim();
  
    // Ensure space after punctuation if missing (e.g. "Hello,world" => "Hello, world")
    cleaned = cleaned.replace(/([.,!?])([^\s])/g, "$1 $2");
  
    // Remove space before punctuation (e.g. "Hello !" => "Hello!")
    cleaned = cleaned.replace(/\s([.,!?])/g, "$1");
  
    return cleaned;
  }
  

  const getSortableValue = (fault, column) => {
    switch (column) {
      case "zenReference":
        return fault.service.zenReference.toLowerCase();
      case "faultReference":
        return fault.faultDetails.faultReference.toLowerCase();
      case "cli":
        return fault.service.cli.toLowerCase();
      case "raisedDate":
        return new Date(fault.faultDetails.raisedDate);
      case "lastUpdated":
        return new Date(fault.faultDetails.lastModifiedDate);
      case "status":
        return fault.faultDetails.status.toLowerCase();
      default:
        return "";
    }
  };

  const sortedFaults = (faults) => {
    if (!sortColumn) return faults;

    return [...faults].sort((a, b) => {
      const aValue = getSortableValue(a, sortColumn);
      const bValue = getSortableValue(b, sortColumn);

      if (aValue < bValue) {
        return sortDirection === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === "ascending" ? 1 : -1;
      }
      return 0;
    });
  };

  const filterFaults = (faults) => {
    if (!searchQuery.trim()) return faults;

    const query = searchQuery.toLowerCase();

    return faults.filter((fault) => {
      const { service, faultDetails } = fault;
      return (
        (service.zenReference && service.zenReference.toLowerCase().includes(query)) ||
        (faultDetails.faultReference && faultDetails.faultReference.toLowerCase().includes(query)) ||
        (service.cli && service.cli.toLowerCase().includes(query)) ||
        (faultDetails.status && faultDetails.status.toLowerCase().includes(query)) ||
        (faultDetails.raisedDate && new Date(faultDetails.raisedDate).toLocaleDateString().includes(query)) ||
        (faultDetails.lastModifiedDate && new Date(faultDetails.lastModifiedDate).toLocaleDateString().includes(query))
      );
    });
  };

  const handleClick = (e, titleProps) => {
    const { index } = titleProps;
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      .replace(",", "");
  };

  const getLatestUpdate = (updateHistory) => {
    if (!updateHistory || updateHistory.length === 0) {
      return {
        update: "An update has not yet been provided",
        createdBy: "",
        lastModifiedDate: "",
      };
    }
    const sorted = [...updateHistory].sort(
      (a, b) => new Date(b.lastModifiedDate) - new Date(a.lastModifiedDate)
    );
    return {
      ...sorted[0],
      update: cleanText(sorted[0].update),
      createdBy: cleanText(sorted[0].createdBy)
    };
  };
  

  const truncateUpdate = (update) => {
    if (!update) return "";
    if (update.length > 50) {
      return `${update.substring(0, 50)}... Read more`;
    }
    return update;
  };

  const filteredOpenFaults = filterFaults(openFaults);
  const filteredClosedFaults = filterFaults(closedFaults);

  const sortedOpenFaults = sortedFaults(filteredOpenFaults);
  const sortedClosedFaults = sortedFaults(filteredClosedFaults);

  const paginatedOpenFaults = sortedOpenFaults.slice(
    (openFaultsPage - 1) * itemsPerPage,
    openFaultsPage * itemsPerPage
  );

  const paginatedClosedFaults = sortedClosedFaults.slice(
    (closedFaultsPage - 1) * itemsPerPage,
    closedFaultsPage * itemsPerPage
  );

  if (!permissions?.servicesAccess) {
    return (
      <Panel>
        <Message error header="You don't have permission to view this page" />
      </Panel>
    );
  }

  return (
    <>
      <Dimmer active={isLoading} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>

      <Panel header="Faults">
        {error && (
          <Message error>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        <Segment>
          <Input
            icon="search"
            placeholder="Search faults..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setOpenFaultsPage(1);
              setClosedFaultsPage(1);
            }}
            fluid
            clearable
          />
        </Segment>

        <Segment style={{ minHeight: "300px" }}>
          <h3>Open faults</h3>
          {filteredOpenFaults.length > 0 ? (
            <>
              <Table celled striped sortable>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell
                      sorted={sortColumn === "zenReference" ? sortDirection : null}
                      onClick={() => handleSort("zenReference")}
                    >
                      Zen reference
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      sorted={sortColumn === "faultReference" ? sortDirection : null}
                      onClick={() => handleSort("faultReference")}
                    >
                      Fault reference
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      sorted={sortColumn === "cli" ? sortDirection : null}
                      onClick={() => handleSort("cli")}
                    >
                      CLI
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      sorted={sortColumn === "raisedDate" ? sortDirection : null}
                      onClick={() => handleSort("raisedDate")}
                    >
                      Date raised
                    </Table.HeaderCell>
                    <Table.HeaderCell
                      sorted={sortColumn === "lastUpdated" ? sortDirection : null}
                      onClick={() => handleSort("lastUpdated")}
                    >
                      Last updated
                    </Table.HeaderCell>
                    <Table.HeaderCell>Updated by</Table.HeaderCell>
                    <Table.HeaderCell>Last update</Table.HeaderCell>
                    <Table.HeaderCell
                      sorted={sortColumn === "status" ? sortDirection : null}
                      onClick={() => handleSort("status")}
                    >
                      Status
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {paginatedOpenFaults.map((fault, index) => {
                    const latestUpdate = getLatestUpdate(fault.updateHistory);
                    return (
                      <Table.Row key={`open-${index}`}>
                        <Table.Cell>{fault.service.zenReference}</Table.Cell>
                        <Table.Cell>{fault.faultDetails.faultReference}</Table.Cell>
                        <Table.Cell>{fault.service.cli}</Table.Cell>
                        <Table.Cell>{formatDate(fault.faultDetails.raisedDate)}</Table.Cell>
                        <Table.Cell>{formatDate(fault.faultDetails.lastModifiedDate)}</Table.Cell>
                        <Table.Cell>{cleanText(latestUpdate.createdBy) || "-"}</Table.Cell>
                        <Table.Cell>{renderUpdateText(cleanText(latestUpdate.update), fault.faultDetails.faultReference)}</Table.Cell>
                        <Table.Cell>
                          <Label color="red">{fault.faultDetails.status}</Label>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
              {filteredOpenFaults.length > itemsPerPage && (
                <div style={{ marginTop: "1em", textAlign: "center" }}>
                  <Pagination
                    activePage={openFaultsPage}
                    totalPages={Math.ceil(filteredOpenFaults.length / itemsPerPage)}
                    onPageChange={(e, { activePage }) => setOpenFaultsPage(activePage)}
                  />
                </div>
              )}
            </>
          ) : (
            <Message info style={{ margin: "2em 0" }}>
              <Message.Header>No Open Faults</Message.Header>
              <p>
                There are currently no open faults available to view here. To raise a fault, please
                visit the service in question, and run a diagnostic test. You may then raise a
                fault with the supplier.
              </p>
            </Message>
          )}
        </Segment>

        <Segment style={{ minHeight: "300px" }}>
          <h3>Recently closed faults</h3>
          {filteredClosedFaults.length > 0 ? (
            <>
              <Accordion fluid styled>
                {paginatedClosedFaults.map((fault, index) => {
                  const latestUpdate = getLatestUpdate(fault.updateHistory);
                  return (
                    <React.Fragment key={`closed-${index}`}>
                      <AccordionTitle
                        active={activeIndex === index}
                        index={index}
                        onClick={handleClick}
                      >
                        <Icon name="dropdown" />
                        {fault.service.zenReference} - {fault.faultDetails.faultReference}
                        <Label color="green" ribbon style={{ marginLeft: "1em" }}>
                          {fault.faultDetails.status}
                        </Label>
                      </AccordionTitle>
                      <AccordionContent active={activeIndex === index}>
                        <Grid columns={2} divided>
                          <GridColumn>
                            <p>
                              <strong>Zen Reference:</strong> {fault.service.zenReference}
                            </p>
                            <p>
                              <strong>Fault Reference:</strong> {fault.faultDetails.faultReference}
                            </p>
                            <p>
                              <strong>CLI:</strong> {fault.service.cli}
                            </p>
                            <p>
                              <strong>Date Raised:</strong> {formatDate(fault.faultDetails.raisedDate)}
                            </p>
                          </GridColumn>
                          <GridColumn>
                            <p>
                              <strong>Last Updated:</strong>{" "}
                              {formatDate(fault.faultDetails.lastModifiedDate)}
                            </p>
                            <p>
                              <strong>Updated By:</strong> {latestUpdate.createdBy || "-"}
                            </p>
                            <p>
                              <strong>Status:</strong> {fault.faultDetails.status}
                            </p>
                          </GridColumn>
                        </Grid>
                        <Segment>
                          <h4>Last Update</h4>
                          <p>{cleanText(latestUpdate.update)}</p>
                        </Segment>
                      </AccordionContent>
                    </React.Fragment>
                  );
                })}
              </Accordion>
              {filteredClosedFaults.length > itemsPerPage && (
                <div style={{ marginTop: "1em", textAlign: "center" }}>
                  <Pagination
                    activePage={closedFaultsPage}
                    totalPages={Math.ceil(filteredClosedFaults.length / itemsPerPage)}
                    onPageChange={(e, { activePage }) => setClosedFaultsPage(activePage)}
                  />
                </div>
              )}
            </>
          ) : (
            <Message info style={{ margin: "2em 0" }}>
              <Message.Header>No Closed Faults</Message.Header>
              <p>There are no recently closed faults</p>
            </Message>
          )}
        </Segment>
      </Panel>
    </>
  );
};

export default OpenFaults;
